import React, { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { M365User, GroupMembership, UserLicense, MailboxState } from '../types';
import { useGraphClient } from '../hooks/useGraphClient';
import { useOffboarding } from '../hooks/useOffboarding';
import { getUserProfile, getUserGroups, getUserLicenses } from '../services/graphService';
import { pingExchange } from '../services/exchangeService';
import UserSearch from '../components/Offboarding/UserSearch';
import UserCard from '../components/Offboarding/UserCard';
import MailboxSettings from '../components/Offboarding/MailboxSettings';
import ActionChecklist from '../components/Offboarding/ActionChecklist';
import ProgressBar from '../components/Offboarding/ProgressBar';
import OffboardingReportComponent from '../components/Offboarding/OffboardingReport';
import Modal from '../components/Shared/Modal';
import Spinner from '../components/Shared/Spinner';

/**
 * Page principale d'offboarding
 * Flow : recherche → sélection → checklist → confirmation → exécution → rapport
 */
const Offboarding: React.FC = () => {
  const { accounts } = useMsal();
  const { getClient, getFunctionToken } = useGraphClient();

  // État de la page
  const [selectedUser, setSelectedUser] = useState<M365User | null>(null);
  const [userGroups, setUserGroups] = useState<GroupMembership[]>([]);
  const [userLicenses, setUserLicenses] = useState<UserLicense[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Diagnostic de la chaîne Exchange (Easy Auth + module + Managed Identity)
  const [healthStatus, setHealthStatus] = useState<'idle' | 'checking' | 'ok' | 'ko'>('idle');
  const [healthMessage, setHealthMessage] = useState('');
  const [healthDetails, setHealthDetails] = useState<MailboxState | null>(null);

  // Hook d'offboarding
  const {
    actions,
    isExecuting,
    currentActionIndex,
    report,
    toggleAction,
    updatePopupData,
    executeOffboarding,
    cancelExecution,
    reset,
  } = useOffboarding();

  /** Quand un utilisateur est sélectionné dans la recherche */
  const handleSelectUser = useCallback(
    async (user: M365User) => {
      setIsLoadingUser(true);
      setLoadError(null);
      try {
        const client = await getClient();
        // Charger le profil complet, les groupes et les licences en parallèle
        const [fullProfile, groups, licenses] = await Promise.all([
          getUserProfile(client, user.id),
          getUserGroups(client, user.id),
          getUserLicenses(client, user.id),
        ]);
        setSelectedUser(fullProfile);
        setUserGroups(groups);
        setUserLicenses(licenses);
      } catch (error: any) {
        console.error('Erreur chargement profil:', error);
        setSelectedUser(null);
        setLoadError(error?.message || 'Impossible de charger le profil de cet utilisateur.');
      } finally {
        setIsLoadingUser(false);
      }
    },
    [getClient]
  );

  /**
   * Vérifie la chaîne Exchange sans rien modifier.
   * Un succès prouve d'un coup les trois maillons : Easy Auth accepte le token,
   * le module ExchangeOnlineManagement est présent dans le package, et la Managed
   * Identity est reconnue par Exchange Online.
   */
  const handleHealthCheck = useCallback(async () => {
    setHealthStatus('checking');
    setHealthMessage('');
    setHealthDetails(null);
    try {
      const functionToken = await getFunctionToken();
      // Si une fiche est ouverte, on teste aussi la lecture de SA boîte : c'est ce
      // qui dit si le refus vient de la connexion ou des droits sur les boîtes.
      const identity = selectedUser?.mail || selectedUser?.userPrincipalName;
      const result = await pingExchange(functionToken, identity);
      const details = result.details as MailboxState | undefined;
      setHealthStatus('ok');
      setHealthDetails(details ?? null);
      setHealthMessage(
        `${result.message}${details?.organization ? ` — ${details.organization}` : ''}` +
          `${details?.module ? ` (module ${details.module})` : ''}`
      );
    } catch (error: any) {
      setHealthStatus('ko');
      setHealthMessage(error?.message || 'Échec inconnu');
    }
  }, [getFunctionToken, selectedUser]);

  /** Lance l'exécution après confirmation */
  const handleExecute = useCallback(async () => {
    setShowConfirmModal(false);
    if (!selectedUser) return;

    try {
      const client = await getClient();
      // Token dédié à la Function Exchange : audience api://<clientId>, pas Graph
      const functionToken = await getFunctionToken();
      const executedBy = accounts[0]?.username || 'unknown';

      await executeOffboarding(
        client,
        functionToken,
        selectedUser,
        userGroups,
        userLicenses,
        executedBy
      );
    } catch (error) {
      console.error('Erreur exécution offboarding:', error);
    }
  }, [
    selectedUser,
    getClient,
    getFunctionToken,
    accounts,
    executeOffboarding,
    userGroups,
    userLicenses,
  ]);

  /** Export PDF du rapport via impression du navigateur */
  const handleExportPDF = useCallback(() => {
    // On utilise window.print() qui cible le div #offboarding-report
    // Solution simple et efficace sans dépendance externe
    const printContent = document.getElementById('offboarding-report');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Les valeurs viennent de l'annuaire et de messages d'erreur Exchange : on les
    // échappe avant de les injecter dans le HTML d'impression.
    const esc = (value: string | null | undefined) =>
      String(value ?? '').replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string)
      );

    printWindow.document.write(`
      <html>
        <head>
          <title>Rapport Offboarding - ${report?.targetUser.displayName}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 20px; color: #323130; }
            .header { border-bottom: 2px solid #0078d4; padding-bottom: 10px; margin-bottom: 20px; }
            .header h1 { color: #0078d4; margin: 0; font-size: 20px; }
            .meta { color: #605e5c; font-size: 13px; margin: 8px 0; }
            .action { padding: 8px 0; border-bottom: 1px solid #edebe9; display: flex; align-items: center; gap: 10px; }
            .success { color: #107c10; }
            .error { color: #d13438; }
            .skipped { color: #a19f9d; }
            .error-msg { color: #d13438; font-size: 11px; }
            .info-msg { color: #605e5c; font-size: 11px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Rapport d'offboarding</h1>
            <p class="meta"><strong>${esc(report?.targetUser.displayName)}</strong> (${esc(report?.targetUser.mail)})</p>
            <p class="meta">Exécuté par : ${esc(report?.executedBy)} | Date : ${report ? new Date(report.executedAt).toLocaleString('fr-CH') : ''}</p>
          </div>
          ${report?.actions.map(a => `
            <div class="action">
              <span class="${a.status}">${a.status === 'success' ? '&#10003;' : a.status === 'error' ? '&#10007;' : '&#8212;'}</span>
              <span>${esc(a.label)}</span>
              <span class="${a.status}" style="margin-left:auto">${a.status === 'success' ? 'Succès' : a.status === 'error' ? 'Erreur' : 'Ignoré'}</span>
            </div>
            ${a.errorMessage ? `<p class="error-msg" style="margin-left:24px">${esc(a.errorMessage)}</p>` : ''}
            ${a.infoMessage ? `<p class="info-msg" style="margin-left:24px">${esc(a.infoMessage)}</p>` : ''}
          `).join('')}
          <p style="margin-top:30px;color:#a19f9d;font-size:11px">Généré par IT Portal</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [report]);

  /** Reset complet pour un nouvel offboarding */
  const handleReset = useCallback(() => {
    reset();
    setSelectedUser(null);
    setUserGroups([]);
    setUserLicenses([]);
    setLoadError(null);
  }, [reset]);

  const checkedCount = actions.filter((a) => a.checked).length;

  return (
    <div className="max-w-4xl">
      {/* Titre de la page */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-ms-gray-800">Offboarding M365</h2>
          <p className="text-sm text-ms-gray-500 mt-1">
            Désactivation complète d'un compte utilisateur Microsoft 365
          </p>
        </div>
        <button
          onClick={handleHealthCheck}
          disabled={healthStatus === 'checking' || isExecuting}
          className="flex-shrink-0 px-3 py-1.5 text-xs text-ms-gray-600 bg-white border border-ms-gray-300 rounded-md hover:bg-ms-gray-50 transition-colors disabled:opacity-50"
        >
          {healthStatus === 'checking' ? 'Vérification…' : 'Vérifier la connexion Exchange'}
        </button>
      </div>

      {/* Résultat du diagnostic Exchange */}
      {healthStatus === 'ok' && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-3">
          <p className="text-sm text-green-700">✓ {healthMessage}</p>

          {/* État réel de la boîte, lu dans Exchange : dit ce qui est DÉJÀ en place
              avant de décider quoi cocher. */}
          {healthDetails?.mailbox && (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs text-ms-gray-600">
              <dt className="text-ms-gray-400">Boîte</dt>
              <dd>
                {healthDetails.mailboxType}
                {healthDetails.mailboxSize ? ` — ${healthDetails.mailboxSize}` : ''}
              </dd>

              <dt className="text-ms-gray-400">Masquée de la GAL</dt>
              <dd>{healthDetails.hiddenFromGAL ? 'oui' : 'non'}</dd>

              <dt className="text-ms-gray-400">Message d'absence</dt>
              <dd>
                {healthDetails.autoReplyState === 'Disabled'
                  ? 'désactivé'
                  : healthDetails.autoReplyState === 'Enabled'
                  ? 'actif en permanence'
                  : healthDetails.autoReplyState === 'Scheduled'
                  ? 'programmé'
                  : healthDetails.autoReplyState || '—'}
              </dd>

              <dt className="text-ms-gray-400">Transfert</dt>
              <dd>
                {healthDetails.forwardingAddress
                  ? `${healthDetails.forwardingAddress}${
                      healthDetails.deliverAndForward ? ' (copie conservée)' : ' (sans copie)'
                    }`
                  : 'aucun'}
              </dd>

              <dt className="text-ms-gray-400">Délégations</dt>
              <dd>
                {healthDetails.delegates?.length
                  ? healthDetails.delegates.join(', ')
                  : 'aucune'}
              </dd>
            </dl>
          )}
        </div>
      )}
      {healthStatus === 'ko' && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-ms-red">
            ✗ Chaîne Exchange indisponible — les actions de boîte mail échoueront.
          </p>
          <p className="text-xs text-ms-red mt-1">{healthMessage}</p>
        </div>
      )}

      {/* Étape 1 : Recherche (masquée si rapport affiché) */}
      {!report && (
        <div className="space-y-6">
          <UserSearch onSelectUser={handleSelectUser} />

          {/* Loading */}
          {isLoadingUser && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
              <span className="ml-3 text-sm text-ms-gray-500">
                Chargement du profil utilisateur...
              </span>
            </div>
          )}

          {/* Erreur de chargement — sinon l'admin reste devant un écran muet */}
          {loadError && !isLoadingUser && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-ms-red">{loadError}</p>
            </div>
          )}

          {/* Étape 2 : Fiche utilisateur + checklist */}
          {selectedUser && !isLoadingUser && (
            <>
              <UserCard user={selectedUser} groups={userGroups} licenses={userLicenses} />

              {/* Entretien courant de la boîte, indépendant de la séquence d'offboarding */}
              <MailboxSettings user={selectedUser} disabled={isExecuting} />

              {/* Progression + arrêt pendant l'exécution */}
              {isExecuting && (
                <div className="space-y-3">
                  <ProgressBar actions={actions} currentIndex={currentActionIndex} />
                  <div className="flex justify-end">
                    <button
                      onClick={cancelExecution}
                      className="px-4 py-2 text-sm text-ms-gray-600 bg-white border border-ms-gray-300 rounded-md hover:bg-ms-gray-50 transition-colors"
                    >
                      Arrêter après l'action en cours
                    </button>
                  </div>
                </div>
              )}

              <ActionChecklist
                actions={actions}
                onToggle={toggleAction}
                onUpdatePopupData={updatePopupData}
                disabled={isExecuting}
                targetUserId={selectedUser.id}
              />

              {/* Bouton d'exécution */}
              {!isExecuting && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-ms-gray-500">
                    {checkedCount} action(s) sélectionnée(s) sur {actions.length}
                  </p>
                  <button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={checkedCount === 0}
                    className="px-6 py-2.5 bg-ms-red text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Lancer l'offboarding
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Étape 3 : Rapport final */}
      {report && (
        <OffboardingReportComponent
          report={report}
          onExportPDF={handleExportPDF}
          onReset={handleReset}
        />
      )}

      {/* Modale de confirmation */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmer l'offboarding"
        onConfirm={handleExecute}
        confirmLabel="Confirmer et exécuter"
        confirmVariant="danger"
      >
        <div>
          <p className="text-sm text-ms-gray-700 mb-3">
            Vous êtes sur le point d'exécuter l'offboarding de :
          </p>
          <div className="bg-ms-gray-50 rounded-md p-3 mb-3">
            <p className="font-medium text-ms-gray-800">{selectedUser?.displayName}</p>
            <p className="text-sm text-ms-gray-500">{selectedUser?.mail}</p>
          </div>
          <p className="text-sm text-ms-gray-700 mb-2">
            <strong>{checkedCount}</strong> action(s) seront exécutées :
          </p>
          <ul className="text-sm text-ms-gray-600 space-y-1 mb-3">
            {actions
              .filter((a) => a.checked)
              .map((a) => (
                <li key={a.id} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-ms-blue rounded-full" />
                  {a.label}
                </li>
              ))}
          </ul>
          {/* Après conversion en boîte partagée, les identifiants d'origine continuent
              de fonctionner sur la boîte. Sans reset du mot de passe NI désactivation,
              la personne qui part garde donc l'accès. */}
          {actions.find((a) => a.id === 'convertSharedMailbox')?.checked &&
            !actions.find((a) => a.id === 'resetPassword')?.checked &&
            !actions.find((a) => a.id === 'disableAccount')?.checked && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                <p className="text-sm text-ms-red">
                  <strong>L'accès ne sera pas coupé :</strong> après conversion en boîte
                  partagée, l'identifiant et le mot de passe d'origine continuent de
                  fonctionner sur la boîte. Cochez le reset du mot de passe ou la
                  désactivation du compte.
                </p>
              </div>
            )}

          {/* Combinaison destructrice : retirer la licence sans convertir la boîte
              en partagée la fait supprimer au bout de 30 jours. */}
          {actions.find((a) => a.id === 'removeLicenses')?.checked &&
            !actions.find((a) => a.id === 'convertSharedMailbox')?.checked && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                <p className="text-sm text-ms-red">
                  <strong>Risque de perte de la boîte mail :</strong> vous retirez les licences
                  sans convertir la boîte en boîte partagée. Une boîte utilisateur sans licence
                  est supprimée au bout de 30 jours. Ne continuez que si la boîte est déjà
                  partagée ou si sa suppression est voulue.
                </p>
              </div>
            )}

          <div className="bg-ms-yellow/10 border border-ms-yellow/30 rounded-md p-3">
            <p className="text-sm text-ms-gray-700">
              <strong>Attention :</strong> Certaines actions sont irréversibles.
              Assurez-vous que cet offboarding est autorisé.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Offboarding;
